import { useEffect, useMemo, useState } from "react";
import api, { downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Wallet, Users, TrendingUp, Clock, ArrowUpDown, X,
} from "lucide-react";
import { toast } from "sonner";

function brl(n) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { const [y, m, d] = iso.substring(0, 10).split("-"); return `${d}/${m}/${y}`; }
  catch { return iso; }
}

function monthLabel(comp) {
  if (!comp) return "—";
  const [y, m] = comp.split("-");
  const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${names[parseInt(m, 10) - 1] || m}/${y}`;
}

const ROLE_COLORS = {
  cirurgiao: "bg-blue-50 text-blue-700 border-blue-200",
  auxiliar: "bg-violet-50 text-violet-700 border-violet-200",
  anestesista: "bg-amber-50 text-amber-700 border-amber-200",
};

const ROLE_LABELS = { cirurgiao: "Cirurgião / Auxiliar", auxiliar: "Auxiliar", anestesista: "Anestesista" };

export default function FinanceiroPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [competence, setCompetence] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("gross"); // default: bruto recebido
  const [sortDir, setSortDir] = useState("desc");
  const [detail, setDetail] = useState(null); // {role, id, name, ...}
  const [detailLoading, setDetailLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalId, setHospitalId] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (competence) params.competence = competence;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (hospitalId) params.hospital_id = hospitalId;
      const r = await api.get("/financial/summary", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
  }, []);

  function applyFilters(e) { e?.preventDefault?.(); load(); }

  function clearFilters() {
    setCompetence("");
    setDateFrom("");
    setDateTo("");
    setRoleFilter("all");
    setSearch("");
    setHospitalId("");
    setTimeout(load, 0);
  }

  const people = useMemo(() => {
    let rows = data?.people || [];
    if (roleFilter !== "all") rows = rows.filter((p) => p.role === roleFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rows = rows.filter((p) => (p.name || "").toLowerCase().includes(s));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return rows;
  }, [data, roleFilter, search, sortKey, sortDir]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  }

  async function exportFull() {
    try {
      const params = {};
      if (competence) params.competence = competence;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (hospitalId) params.hospital_id = hospitalId;
      const suffix = competence || (dateFrom || dateTo ? `${dateFrom || "inicio"}_a_${dateTo || "fim"}` : "geral");
      await downloadFile("/financial/export.xlsx", `financeiro_${suffix}.xlsx`, params);
      toast.success("Relatório financeiro gerado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function openDetail(person) {
    setDetailLoading(true);
    setDetail({ role: person.role, id: person.id, name: person.name, loading: true });
    try {
      const params = { role: person.role };
      if (person.id) params.id = person.id;
      if (competence) params.competence = competence;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/financial/person", { params });
      setDetail({ ...r.data, role: person.role, id: person.id, name: person.name });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function exportPerson() {
    if (!detail?.id) { toast.error("Esta pessoa não tem cadastro — exporte pela planilha geral."); return; }
    try {
      const params = { role: detail.role, id: detail.id };
      if (competence) params.competence = competence;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      await downloadFile(
        "/financial/person/export.xlsx",
        `financeiro_${detail.role}_${(detail.name || "").replace(/\s+/g, "_")}.xlsx`,
        params,
      );
      toast.success("Planilha individual gerada.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const totals = data?.totals || { cases: 0, gross: 0, shifts: 0, pro_labore: 0, pro_labore_tax: 0, profit: 0 };
  const proLaboreRate = data?.pro_labore_per_shift ?? 0;
  const proLaboreTaxRate = data?.pro_labore_tax_rate ?? 0;

  return (
    <div className="space-y-5" data-testid="financeiro-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Financeiro</h1>
          <p className="text-sm text-slate-500 mt-1">
            Rateio por turno · Pro-labore: <strong>{brl(proLaboreRate)}</strong>/turno · Imposto sobre pro-labore: <strong>{(proLaboreTaxRate * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%</strong>
          </p>
        </div>
        <Button onClick={exportFull} className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9 w-full sm:w-auto" data-testid="btn-export-financial">
          <Download className="h-4 w-4 mr-2" /> Exportar Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Competência (YYYY-MM)</Label>
              <Input type="month" value={competence} onChange={(e) => setCompetence(e.target.value)}
                className="w-full sm:w-40 mt-1" data-testid="filter-competence" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">De</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="w-full sm:w-40 mt-1" data-testid="filter-date-from" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Até</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="w-full sm:w-40 mt-1" data-testid="filter-date-to" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Papel</Label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full sm:w-40 block mt-1 border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
                data-testid="filter-role">
                <option value="all">Todos</option>
                <option value="cirurgiao">Cirurgião</option>
                <option value="auxiliar">Auxiliar</option>
                <option value="anestesista">Anestesista</option>
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hospital</Label>
              <select value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}
                className="w-full sm:w-56 block mt-1 border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
                data-testid="filter-hospital">
                <option value="">Todos</option>
                {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Buscar nome</Label>
              <Input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Ex: André" className="w-48 mt-1" data-testid="filter-search" />
            </div>
            <div className="flex gap-2 ml-auto">
              <Button type="submit" className="bg-slate-800 hover:bg-slate-900" data-testid="btn-apply-filters">Aplicar</Button>
              <Button type="button" variant="outline" onClick={clearFilters} data-testid="btn-clear-filters">
                <X className="h-4 w-4 mr-1" /> Limpar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <KPI icon={<Users className="h-3.5 w-3.5" />} label="Casos" value={totals.cases} testid="kpi-cases" />
        <KPI icon={<Clock className="h-3.5 w-3.5" />} label="Turnos" value={totals.shifts} testid="kpi-shifts" />
        <KPI icon={<Wallet className="h-3.5 w-3.5" />} label="Faturamento equipe" value={brl(totals.gross)} testid="kpi-gross" />
        <KPI icon={<Wallet className="h-3.5 w-3.5" />} label="Pro-labore" value={brl(totals.pro_labore)} testid="kpi-pro-labore" tone="amber" />
        <KPI icon={<Wallet className="h-3.5 w-3.5" />} label={`Imposto pro-labore (${(proLaboreTaxRate * 100).toFixed(1)}%)`} value={brl(totals.pro_labore_tax || 0)} testid="kpi-pro-labore-tax" tone="rose" />
        <KPI icon={<TrendingUp className="h-3.5 w-3.5" />} label="Lucro líquido" value={brl(totals.profit)} testid="kpi-profit" tone="emerald" />
      </div>

      <div className="text-xs px-3 py-2 rounded-md bg-blue-50 text-blue-800 border border-blue-200" data-testid="pro-labore-rule-hint">
        <strong>Como o lucro é calculado:</strong> Faturamento equipe − Pro-labore − Imposto sobre pro-labore ({(proLaboreTaxRate * 100).toFixed(1)}% do pro-labore). O imposto é deduzido do lucro de cada pessoa proporcionalmente aos turnos trabalhados.
      </div>

      {totals.profit < 0 && (
        <div className="text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-800 border border-amber-200" data-testid="profit-negative-hint">
          O lucro está negativo porque o total de pro-labore (R$ {(Number(proLaboreRate) || 0).toLocaleString("pt-BR")}/turno × turnos) supera o faturamento da equipe no recorte atual.
          Revise os valores líquidos em <strong>Procedimentos → aba Financeiro</strong> ou ajuste o pro-labore em <strong>Configurações</strong>.
        </div>
      )}

      {/* By Competence */}
      {data?.by_month?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="font-display text-lg font-semibold text-slate-900">Por competência</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Mês</th>
                    <th className="text-right px-3 py-2">Casos</th>
                    <th className="text-right px-3 py-2">Turnos</th>
                    <th className="text-right px-3 py-2">Faturamento equipe</th>
                    <th className="text-right px-3 py-2">Pro-labore</th>
                    <th className="text-right px-3 py-2">Imposto</th>
                    <th className="text-right px-3 py-2">Lucro</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_month.map((m) => (
                    <tr key={m.month} className="border-t border-slate-100" data-testid={`month-row-${m.month}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{monthLabel(m.month)}</td>
                      <td className="px-3 py-2 text-right">{m.cases}</td>
                      <td className="px-3 py-2 text-right">{m.shifts}</td>
                      <td className="px-3 py-2 text-right">{brl(m.gross)}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{brl(m.pro_labore)}</td>
                      <td className="px-3 py-2 text-right text-rose-700">{brl(m.pro_labore_tax || 0)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">{brl(m.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Period */}
      {data?.by_period?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="font-display text-lg font-semibold text-slate-900">Por período do dia</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {data.by_period.map((p) => (
                <div key={p.period} className="border border-slate-200 rounded-md p-4" data-testid={`period-${p.period}`}>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{p.period_label}</div>
                  <div className="mt-1 text-2xl font-display font-bold text-slate-900">{brl(p.gross)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.cases} caso(s)</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* People */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="font-display text-lg font-semibold text-slate-900">Ranking por pessoa</h2>
            <div className="text-xs text-slate-500">{people.length} registro(s) · clique na linha para ver detalhe</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Papel</th>
                <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>Nome</Th>
                <Th onClick={() => toggleSort("cases")} active={sortKey === "cases"} dir={sortDir} align="right">Casos</Th>
                <Th onClick={() => toggleSort("shifts")} active={sortKey === "shifts"} dir={sortDir} align="right">Turnos</Th>
                <Th onClick={() => toggleSort("gross")} active={sortKey === "gross"} dir={sortDir} align="right">Faturamento equipe</Th>
                <Th onClick={() => toggleSort("pro_labore")} active={sortKey === "pro_labore"} dir={sortDir} align="right">Pro-labore</Th>
                <Th onClick={() => toggleSort("pro_labore_tax")} active={sortKey === "pro_labore_tax"} dir={sortDir} align="right">Imposto</Th>
                <Th onClick={() => toggleSort("profit")} active={sortKey === "profit"} dir={sortDir} align="right">Lucro</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center p-6 text-slate-400">Carregando…</td></tr>}
              {!loading && people.length === 0 && (
                <tr><td colSpan={8} className="text-center p-8 text-slate-400">Sem dados para os filtros aplicados.</td></tr>
              )}
              {people.map((p, idx) => (
                <tr key={`${p.role}-${p.id || p.name}-${idx}`}
                  className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                  onClick={() => openDetail(p)}
                  data-testid={`person-row-${idx}`}>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ROLE_COLORS[p.role] || ""}`}>
                      {ROLE_LABELS[p.role] || p.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {p.name}
                    {p.is_team_member === true && <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-700">Equipe</span>}
                  </td>
                  <td className="px-4 py-3 text-right">{p.cases}</td>
                  <td className="px-4 py-3 text-right">{p.shifts}</td>
                  <td className="px-4 py-3 text-right font-medium">{brl(p.gross)}</td>
                  <td className="px-4 py-3 text-right text-amber-700">{brl(p.pro_labore)}</td>
                  <td className="px-4 py-3 text-right text-rose-700">{brl(p.pro_labore_tax || 0)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{brl(p.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {detail && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ROLE_COLORS[detail.role] || ""}`}>
                    {ROLE_LABELS[detail.role] || detail.role}
                  </span>
                  <span>{detail.name}</span>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailLoading && <div className="p-6 text-center text-slate-400">Carregando detalhes…</div>}
          {detail && !detailLoading && (
            <div className="overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <KPI label="Casos" value={detail.cases} />
                <KPI label="Turnos" value={detail.shifts} />
                <KPI label="Faturamento equipe" value={brl(detail.gross)} />
                <KPI label="Pro-labore" value={brl(detail.pro_labore)} tone="amber" />
                <KPI label={`Imposto ${(detail.pro_labore_tax_rate ? detail.pro_labore_tax_rate * 100 : 0).toFixed(1)}%`} value={brl(detail.pro_labore_tax || 0)} tone="rose" />
                <KPI label="Lucro líquido" value={brl(detail.profit)} tone="emerald" />
              </div>
              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Código</th>
                      <th className="text-left px-3 py-2">Data</th>
                      <th className="text-left px-3 py-2">Período</th>
                      <th className="text-left px-3 py-2">Paciente</th>
                      <th className="text-left px-3 py-2">Procedimento</th>
                      <th className="text-center px-3 py-2" title="Quantos cirurgiões dividiram a fatia da equipe neste caso">Equipe</th>
                      <th className="text-right px-3 py-2">Bruto BVSM</th>
                      <th className="text-right px-3 py-2">Sua parte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.details || []).length === 0 && (
                      <tr><td colSpan={8} className="text-center p-6 text-slate-400">Sem casos nessa faixa.</td></tr>
                    )}
                    {(detail.details || []).map((d, i) => (
                      <tr key={d.case_code || `${d.patient_name}-${i}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs">{d.case_code}</td>
                        <td className="px-3 py-2">{fmtDate(d.date)}</td>
                        <td className="px-3 py-2 capitalize">{d.period}</td>
                        <td className="px-3 py-2">{d.patient_name || "—"}</td>
                        <td className="px-3 py-2">{d.procedure || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {d.n_surgeons ? (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                d.n_surgeons === 1
                                  ? "bg-amber-50 text-amber-800 border border-amber-200"
                                  : d.n_surgeons === 2
                                    ? "bg-blue-50 text-blue-800 border border-blue-200"
                                    : "bg-violet-50 text-violet-800 border border-violet-200"
                              }`}
                              title={`${d.n_surgeons} cirurgião(ões) dividiram a fatia da equipe — divisor ÷${d.n_surgeons}`}
                              data-testid={`badge-n-surgeons-${i}`}
                            >
                              👥 {d.n_surgeons}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-slate-500">{d.case_gross != null ? brl(d.case_gross) : "—"}</td>
                        <td className="px-3 py-2 text-right font-medium">{brl(d.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="btn-close-detail">Fechar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={exportPerson} disabled={!detail?.id} data-testid="btn-export-person">
              <Download className="h-4 w-4 mr-2" /> Exportar Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon, label, value, testid, tone }) {
  const color =
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber" ? "text-amber-700" :
    tone === "rose" ? "text-rose-700" :
    "text-slate-900";
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
          {icon}{label}
        </div>
        <div className={`text-2xl font-display font-bold mt-1 ${color}`} data-testid={testid}>{value}</div>
      </CardContent>
    </Card>
  );
}

function Th({ children, onClick, active, dir, align = "left" }) {
  return (
    <th className={`px-4 py-3 text-${align} cursor-pointer select-none`} onClick={onClick}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${active ? "text-blue-700" : "text-slate-400"}`} />
        {active && <span className="text-[10px] text-blue-700">{dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}
